//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import storageService from './utils/storageService';

export interface ActiveLoopContextType {
  activeLoopId: string | null;
  activeLoopName: string | null;
  activeTeamspaceId: string | null;
  isLoopContextActive: boolean;
  isTeamspaceContextActive: boolean;
  setLoopContext: (
    loopId: string,
    loopName: string,
    teamspaceId: string,
  ) => void;
  setTeamspaceContext: (teamspaceId: string) => void;
  clearLoopContext: () => void;
}

const ActiveLoopContext = createContext<ActiveLoopContextType | undefined>(
  undefined,
);

export const useActiveLoopContext = () => {
  const context = useContext(ActiveLoopContext);
  if (context === undefined) {
    throw new Error(
      'useActiveLoopContext must be used within an ActiveLoopProvider',
    );
  }
  return context;
};

interface ActiveLoopProviderProps {
  children: ReactNode;
}

// Read persisted loop context from sessionStorage so it survives page reloads
// within the same tab. sessionStorage is per-tab, giving each tab independent
// context. New tabs opened via target="_blank" inherit a copy of the opener's
// sessionStorage, so "View Resource" links still carry context.
//
// Fallback: when sessionStorage is empty (e.g. new tab opened with noopener),
// read from URL search params — the app switcher appends context params to the
// target URL so context survives cross-tab navigation.
const getPersistedLoopId = (): string | null =>
  storageService.getSessionItem(storageService.KEYS.ACTIVE_LOOP_ID);
const getPersistedLoopName = (): string | null =>
  storageService.getSessionItem(storageService.KEYS.ACTIVE_LOOP_NAME);
const getPersistedTeamspaceId = (): string | null =>
  storageService.getSessionItem(storageService.KEYS.ACTIVE_TEAMSPACE_ID);

function getInitialLoopId(): string | null {
  const persisted = getPersistedLoopId();
  if (persisted) return persisted;
  try {
    return new URLSearchParams(window.location.search).get('loopId') || null;
  } catch {
    return null;
  }
}

function getInitialLoopName(): string | null {
  const persisted = getPersistedLoopName();
  if (persisted) return persisted;
  try {
    return new URLSearchParams(window.location.search).get('loopName') || null;
  } catch {
    return null;
  }
}

function getInitialTeamspaceId(): string | null {
  const persisted = getPersistedTeamspaceId();
  if (persisted) return persisted;
  try {
    return (
      new URLSearchParams(window.location.search).get('teamspaceId') || null
    );
  } catch {
    return null;
  }
}

export const ActiveLoopProvider: React.FC<ActiveLoopProviderProps> = ({
  children,
}) => {
  const [activeLoopId, setActiveLoopId] = useState<string | null>(
    getInitialLoopId,
  );
  const [activeLoopName, setActiveLoopName] = useState<string | null>(
    getInitialLoopName,
  );
  const [activeTeamspaceId, setActiveTeamspaceId] = useState<string | null>(
    getInitialTeamspaceId,
  );

  const setLoopContext = useCallback(
    (loopId: string, loopName: string, teamspaceId: string) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ActiveLoopContext] Setting loop context:', {
          loopId,
          loopName,
          teamspaceId,
          timestamp: new Date().toISOString(),
        });
      }
      setActiveLoopId(loopId);
      setActiveLoopName(loopName);
      setActiveTeamspaceId(teamspaceId);
      storageService.setSessionItem(storageService.KEYS.ACTIVE_LOOP_ID, loopId);
      storageService.setSessionItem(
        storageService.KEYS.ACTIVE_LOOP_NAME,
        loopName,
      );
      storageService.setSessionItem(
        storageService.KEYS.ACTIVE_TEAMSPACE_ID,
        teamspaceId,
      );
    },
    [],
  );

  const setTeamspaceContext = useCallback((teamspaceId: string) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[ActiveLoopContext] Setting teamspace-only context:', {
        teamspaceId,
        timestamp: new Date().toISOString(),
      });
    }
    setActiveLoopId(null);
    setActiveLoopName(null);
    setActiveTeamspaceId(teamspaceId);
    storageService.removeSessionItem(storageService.KEYS.ACTIVE_LOOP_ID);
    storageService.removeSessionItem(storageService.KEYS.ACTIVE_LOOP_NAME);
    storageService.setSessionItem(
      storageService.KEYS.ACTIVE_TEAMSPACE_ID,
      teamspaceId,
    );
  }, []);

  const clearLoopContext = useCallback(() => {
    setActiveLoopId(null);
    setActiveLoopName(null);
    setActiveTeamspaceId(null);
    storageService.removeSessionItem(storageService.KEYS.ACTIVE_LOOP_ID);
    storageService.removeSessionItem(storageService.KEYS.ACTIVE_LOOP_NAME);
    storageService.removeSessionItem(storageService.KEYS.ACTIVE_TEAMSPACE_ID);
  }, []);

  const isLoopContextActive = activeLoopId !== null && activeLoopName !== null;
  const isTeamspaceContextActive = activeTeamspaceId !== null;

  const value = useMemo(
    () => ({
      activeLoopId,
      activeLoopName,
      activeTeamspaceId,
      isLoopContextActive,
      isTeamspaceContextActive,
      setLoopContext,
      setTeamspaceContext,
      clearLoopContext,
    }),
    [
      activeLoopId,
      activeLoopName,
      activeTeamspaceId,
      isLoopContextActive,
      isTeamspaceContextActive,
      setLoopContext,
      setTeamspaceContext,
      clearLoopContext,
    ],
  );

  return (
    <ActiveLoopContext.Provider value={value}>
      {children}
    </ActiveLoopContext.Provider>
  );
};
